// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CarFront, Plus, Search, Shield } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Button,
  Card,
  Badge,
  EmptyState,
  SetupGuide,
  Input,
  Select,
  Pagination,
  DataTable,
  Skeleton,
  ConfirmDialog,
} from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import { usePropertyPermits, useUpdatePermit } from '@/hooks/useParkingPermits'
import { useListSort } from '@/hooks/useListSort'
import { useProperty } from '@/hooks/useProperties'
import { useVehicles } from '@/hooks/useVehicles'
import { useZones } from '@/hooks/useParkingZones'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import type { ParkingPermit, PermitType, PermitStatus } from '@/types/parking'

type StatusFilter = '' | PermitStatus
type PermitTypeFilter = '' | PermitType

const PERMIT_TYPE_BADGE_VARIANTS: Record<PermitType, 'success' | 'info' | 'warning' | 'ghost'> = {
  tenant_included: 'success',
  tenant_addon: 'info',
  monthly_external: 'warning',
  daily_external: 'ghost',
}

const STATUS_BADGE_VARIANTS: Record<PermitStatus, 'success' | 'ghost' | 'error'> = {
  active: 'success',
  expired: 'ghost',
  cancelled: 'error',
}

export function ParkingPermitListPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()
  const { timezone } = useLocalizationConfig()
  const { propertyId } = useParams<{ propertyId: string }>()
  const navigate = useNavigate()
  const numericPropertyId = Number(propertyId)

  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('')
  const [permitTypeFilter, setPermitTypeFilter] = useState<PermitTypeFilter>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [cancelTarget, setCancelTarget] = useState<ParkingPermit | null>(null)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data: propertyData } = useProperty(numericPropertyId)
  const { data, isLoading, error, isFetching } = usePropertyPermits(numericPropertyId, {
    page,
    status: statusFilter || undefined,
    permit_type: permitTypeFilter || undefined,
    q: searchQuery || undefined,
    ...sortParams,
  })
  // Unfiltered query to decide between setup guide and empty state
  const { data: totalData } = usePropertyPermits(numericPropertyId, { page: 1 })
  const totalCount = totalData?.meta?.total_count ?? 0

  // Prerequisite data
  const { data: vehiclesData } = useVehicles(numericPropertyId, { page: 1 })
  const { data: zonesData } = useZones(numericPropertyId)
  const hasVehicles = (vehiclesData?.meta?.total_count ?? 0) > 0
  const hasZones = (zonesData?.data?.length ?? 0) > 0

  const updatePermit = useUpdatePermit()

  const permits = data?.data || []
  const property = propertyData

  const statusTabs: { value: StatusFilter; label: string }[] = [
    { value: '', label: t('filters.all') },
    { value: 'active', label: t('filters.active') },
    { value: 'expired', label: t('filters.expired') },
    { value: 'cancelled', label: t('filters.cancelled') },
  ]

  const permitTypeOptions = [
    { value: '', label: t('filters.all_types') },
    { value: 'tenant_included', label: t('permits.types.tenant_included') },
    { value: 'tenant_addon', label: t('permits.types.tenant_addon') },
    { value: 'monthly_external', label: t('permits.types.monthly_external') },
    { value: 'daily_external', label: t('permits.types.daily_external') },
  ]

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return
    const today = localToday(timezone)
    await updatePermit.mutateAsync({
      id: cancelTarget.id,
      status: 'cancelled',
      ended_at: today,
    })
    setCancelTarget(null)
  }

  const getFeeDisplay = (permit: ParkingPermit) => {
    if (permit.permit_type === 'daily_external') {
      return permit.daily_fee != null ? `฿${permit.daily_fee}/day` : '-'
    }
    return permit.monthly_fee != null ? `฿${permit.monthly_fee}/mo` : '-'
  }

  const columns: Column<ParkingPermit>[] = [
    {
      key: 'permit_type',
      header: t('permits.permit_type'),
      render: (p) => (
        <Badge variant={PERMIT_TYPE_BADGE_VARIANTS[p.permit_type]}>
          {t(`permits.types.${p.permit_type}`)}
        </Badge>
      ),
    },
    {
      key: 'vehicle',
      header: t('labels.license_plate'),
      render: (p) =>
        p.vehicle ? (
          <Link
            to={`/properties/${numericPropertyId}/parking/vehicles/${p.vehicle_id}/edit`}
            className="link link-primary font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            {p.vehicle.license_plate}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      key: 'zone',
      header: t('zones.name'),
      render: (p) => p.parking_zone?.name || '-',
    },
    {
      key: 'slot_number',
      header: t('permits.slot_number'),
      sortable: true,
      render: (p) => p.slot_number || '-',
    },
    {
      key: 'sticker_number',
      header: t('permits.sticker_number'),
      render: (p) => p.sticker_number || '-',
    },
    {
      key: 'fee',
      header: t('labels.fee'),
      render: (p) => getFeeDisplay(p),
    },
    {
      key: 'started_at',
      header: t('permits.started_at'),
      sortable: true,
      render: (p) => formatDate(p.started_at),
    },
    {
      key: 'ended_at',
      header: t('permits.ended_at'),
      sortable: true,
      render: (p) => (p.ended_at ? formatDate(p.ended_at) : '-'),
    },
    {
      key: 'status',
      header: tc('labels.status'),
      render: (p) => (
        <Badge variant={STATUS_BADGE_VARIANTS[p.status]}>
          {t(`permits.statuses.${p.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (p) =>
        p.status === 'active' ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-error"
            onClick={(e) => {
              e.stopPropagation()
              setCancelTarget(p)
            }}
          >
            {t('permits.cancel_permit')}
          </Button>
        ) : null,
    },
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns. Labels
  // reuse the column-header translations; the default updated_at gets a "Last updated"
  // label since it has no visible column.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'slot_number', label: t('permits.slot_number') },
    { value: 'started_at', label: t('permits.started_at') },
    { value: 'ended_at', label: t('permits.ended_at') },
  ]

  const breadcrumbItems = [
    { label: tc('nav.properties'), href: '/properties' },
    {
      label: property?.name || String(numericPropertyId),
      href: `/properties/${numericPropertyId}`,
    },
    { label: t('permits.title') },
  ]

  const activePermitFilterCount = [statusFilter, permitTypeFilter].filter(Boolean).length

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={breadcrumbItems} />

        <PageHeader
          title={t('permits.title')}
          isRefreshing={isFetching && !isLoading}
          actions={
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate(`/properties/${numericPropertyId}/parking/permits/new`)}
            >
              {t('permits.add')}
            </Button>
          }
        />

        {/* Filters */}
        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              label={t('permits.search_placeholder')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('permits.search_placeholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
            />
          </div>

          {/* Mobile-only toggle */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="permit-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activePermitFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activePermitFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="permit-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            {/* Status tabs */}
            <div className="flex gap-1 self-center overflow-x-auto">
              {statusTabs.map((tab) => (
                <Button
                  key={tab.value}
                  size="sm"
                  variant={statusFilter === tab.value ? 'primary' : 'ghost'}
                  className="flex-shrink-0 whitespace-nowrap"
                  onClick={() => {
                    setStatusFilter(tab.value)
                    setPage(1)
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            {/* Permit type filter */}
            <Select
              label={t('permits.permit_type')}
              value={permitTypeFilter}
              onChange={(e) => {
                setPermitTypeFilter(e.target.value as PermitTypeFilter)
                setPage(1)
              }}
              options={permitTypeOptions}
              className="w-full sm:w-52 min-w-0"
            />
          </div>
        </Card>

        {/* Content */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              action={
                <Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>
              }
            />
          </Card>
        ) : !permits.length ? (
          <Card>
            {totalCount === 0 ? (
              <SetupGuide
                icon={<Shield className="w-16 h-16" />}
                title={t('setup_guide.permits.title')}
                description={t('setup_guide.permits.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.permits.prereq_vehicles'),
                    met: hasVehicles,
                    link: `/properties/${numericPropertyId}/parking/vehicles`,
                    linkLabel: t('setup_guide.permits.prereq_setup_vehicles'),
                  },
                  {
                    label: t('setup_guide.permits.prereq_zones'),
                    met: hasZones,
                    link: `/properties/${numericPropertyId}/parking/zones`,
                    linkLabel: t('setup_guide.permits.prereq_setup_zones'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  {
                    title: t('setup_guide.permits.steps.register_vehicle.title'),
                    description: t('setup_guide.permits.steps.register_vehicle.description'),
                  },
                  {
                    title: t('setup_guide.permits.steps.create_zone.title'),
                    description: t('setup_guide.permits.steps.create_zone.description'),
                  },
                  {
                    title: t('setup_guide.permits.steps.issue_permit.title'),
                    description: t('setup_guide.permits.steps.issue_permit.description'),
                  },
                ]}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate(`/properties/${numericPropertyId}/parking/permits/new`)}
                  >
                    {t('permits.add')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<CarFront className="w-12 h-12" />}
                title={t('permits.empty.title')}
                description={t('permits.empty.description')}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate(`/properties/${numericPropertyId}/parking/permits/new`)}
                  >
                    {t('permits.add')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={permits}
              keyExtractor={(p) => p.id}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
            />
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Cancel Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        title={t('permits.cancel_permit')}
        message={t('permits.cancel_confirm')}
        confirmLabel={t('permits.cancel_permit')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={updatePermit.isPending}
      />
    </>
  )
}
